import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, FileQuestion, Award, Eye } from "lucide-react";
import ModuleBody from "../../components/ModuleBody";
import { api, errMsg } from "../../lib/api";
import { PageHeader } from "../../components/common";
import { Progress } from "../../components/ui/progress";

const PreviewBanner = () => (
  <div className="mb-5 rounded-md bg-[#250E31] text-white px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2" data-testid="admin-preview-banner">
    <span className="flex items-center gap-2 font-semibold text-[#D4AF37]"><Eye className="w-4 h-4" />Admin preview</span>
    <span className="text-white/80 flex-1">Every session, video part, quick check and exam is unlocked. Nothing you do here is saved.</span>
    <Link to="/app/admin/content" className="text-[#D4AF37] font-semibold" data-testid="admin-preview-back-link">Back to Course Content</Link>
  </div>
);

const Quiz = ({ slug, quiz, passMark, onDone, preview }) => {
  const [answers, setAnswers] = useState(Array(quiz.length).fill(-1));
  const [result, setResult] = useState(null);
  const submit = async () => {
    try {
      const { data } = await api.post(`/learn/${slug}/quiz`, { answers });
      setResult(data);
      if (data.passed) { toast.success("Congratulations, you passed!"); if (!preview) onDone(); } else toast.error(`Score ${data.score}%. You need ${passMark}%.`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (result?.passed) return (
    <div className="text-center py-8" data-testid="quiz-passed">
      <Award className="w-12 h-12 mx-auto text-[#D4AF37]" />
      <div className="font-display text-2xl font-bold mt-3">You scored {result.score}%</div>
      {result.preview ? <p className="text-sm text-[#6b5a73] mt-1" data-testid="quiz-preview-note">Preview only - a learner would pass and get a certificate. No certificate was issued.</p> : <>
        <p className="text-sm text-[#6b5a73] mt-1">Your certificate has been added to your resume.</p>
        <Link to={`/certificate/${result.certificate_no}`} className="dx-btn-gold mt-5" data-testid="quiz-view-certificate-btn">View certificate</Link>
      </>}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="quiz-form">
      {result && <div className="rounded-xl bg-rose-50 text-rose-700 text-sm p-3" data-testid="quiz-failed">You scored {result.score}% ({result.correct}/{result.total}). Review the modules and try again.</div>}
      {quiz.map((q, i) => (
        <div key={i}>
          <div className="font-semibold">{i + 1}. {q.q}</div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {q.options.map((o, j) => (
              <button key={j} onClick={() => setAnswers(answers.map((a, k) => (k === i ? j : a)))} data-testid={`quiz-q${i}-opt${j}`}
                className={`text-left rounded-xl border px-4 py-2.5 text-sm transition-colors ${answers[i] === j ? "border-[#5B2A6E] bg-[#F4EEF7] font-semibold" : "hover:bg-[#FAF8FC]"}`}>{o}</button>
            ))}
          </div>
        </div>
      ))}
      <button className="dx-btn-gold" disabled={answers.includes(-1)} onClick={submit} data-testid="quiz-submit-btn">Submit exam</button>
    </div>
  );
};

export default function Learn() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const startModule = Number(params.get("module")) || 0;
  const startPart = Number(params.get("part")) || 0;
  const [data, setData] = useState(null);
  const [active, setActive] = useState(startModule);
  const [error, setError] = useState("");

  const load = () => api.get(`/learn/${slug}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div className="dx-card p-8" data-testid="learn-error">{error} <Link to="/app/academy" className="font-semibold text-[#5B2A6E] ml-2">Go to Academy</Link></div>;
  if (!data) return null;
  const { course, enrollment: en } = data;
  const done = en.completed_modules;
  const preview = !!data.preview;
  const allDone = preview || done.length === course.modules.length;
  const isExam = active === course.modules.length;
  const mod = course.modules[active];

  const complete = async () => {
    if (preview || done.includes(mod.id)) return setActive(active + 1);
    try {
      const r = await api.post(`/learn/${slug}/modules/${mod.id}/complete`);
      setData({ ...data, enrollment: r.data });
      setActive(active + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow={`${course.code} · ${course.duration}`} title={course.title} subtitle={`Pass mark ${data.pass_mark}%`} />
      {preview && <PreviewBanner />}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-[#6b5a73] mb-2"><span>Progress</span><span data-testid="learn-progress-text">{done.length}/{course.modules.length} modules</span></div>
        <Progress value={(done.length / course.modules.length) * 100} />
      </div>
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4 dx-card p-3 h-fit">
          {course.modules.map((m, i) => (
            <button key={m.id} onClick={() => setActive(i)} data-testid={`learn-module-${m.id}`}
              className={`w-full text-left flex items-start gap-3 rounded-xl px-3 py-3 text-sm ${active === i ? "bg-[#F4EEF7]" : "hover:bg-[#FAF8FC]"}`}>
              {done.includes(m.id) ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
              <span className="font-medium">{i + 1}. {m.title}</span>
            </button>
          ))}
          <button onClick={() => allDone && setActive(course.modules.length)} disabled={!allDone} data-testid="learn-exam-tab"
            className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold ${isExam ? "bg-[#5B2A6E] text-white" : allDone ? "text-[#5B2A6E]" : "text-slate-400"}`}>
            <FileQuestion className="w-4 h-4" />Final exam {en.status === "completed" && "· Passed"}
          </button>
        </div>
        <div className="lg:col-span-8 dx-card p-6 sm:p-8">
          {isExam ? (en.status === "completed" ? (
            <div className="text-center py-8" data-testid="learn-completed">
              <Award className="w-12 h-12 mx-auto text-[#D4AF37]" />
              <div className="font-display text-2xl font-bold mt-3">Certified · {en.quiz_score}%</div>
              <Link to={`/certificate/${en.certificate_no}`} className="dx-btn-gold mt-5">View certificate</Link>
            </div>
          ) : <Quiz slug={slug} quiz={course.quiz} passMark={data.pass_mark} onDone={load} preview={preview} />) : (
            <article data-testid="learn-module-content">
              <div className="dx-eyebrow">Module {active + 1}</div>
              <h2 className="font-display text-2xl font-bold mt-2 text-[#250E31]">{mod.title}</h2>
              <ModuleBody slug={slug} mod={mod} done={done} onComplete={complete} onPassed={load} preview={preview} initialPart={active === startModule ? startPart : 0} />
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
